import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import AdminShareLinks from "@/pages/AdminShareLinks";
import AdminBackup from "@/pages/AdminBackup";
import AdminAuditLog from "@/pages/AdminAuditLog";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import CreateProject from "@/pages/CreateProject";
import ProjectDetail from "@/pages/ProjectDetail";
import ResourcePlan from "@/pages/ResourcePlan";
import AdminTemplates from "@/pages/AdminTemplates";
import AdminUsers from "@/pages/AdminUsers";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminSuppliers from "@/pages/AdminSuppliers";
import PublicProject from "@/pages/PublicProject";
import PublicReport from "@/pages/PublicReport";
import PublicQualityReport from "@/pages/PublicQualityReport";
import PublicCustomerComplaints from "@/pages/PublicCustomerComplaints";
import PublicCustomerProjects from "@/pages/PublicCustomerProjects";
import Cases from "@/pages/Cases";
import CreateCase from "@/pages/CreateCase";
import CaseDetail from "@/pages/CaseDetail";
import CasePlan from "@/pages/CasePlan";
import TotalPlan from "@/pages/TotalPlan";
import DocumentAccessLog from "@/pages/DocumentAccessLog";
import ReklamationAnalyse from "@/pages/ReklamationAnalyse";
import AnalysisPage from "@/pages/AnalysisPage";
import PublicAnalysis from "@/pages/PublicAnalysis";
import Reports from "@/pages/Reports";
import AdminActivity from "@/pages/AdminActivity";
import QualityReports from "@/pages/QualityReports";
import QualityReportBuilder from "@/pages/QualityReportBuilder";
import Documents from "@/pages/Documents";
import DocumentBuilder from "@/pages/DocumentBuilder";
import AnalyseLayoutPreview from "@/pages/AnalyseLayoutPreview";
import AuditPlan from "@/pages/AuditPlan";
import AuditExecute from "@/pages/AuditExecute";
import AuditHistory from "@/pages/AuditHistory";
import AuditReport from "@/pages/AuditReport";
import AuditFindings from "@/pages/AuditFindings";
import FindingDetail from "@/pages/FindingDetail";
import Ledelse from "@/pages/Ledelse";
import LeMeeting from "@/pages/LeMeeting";
import LeActionDetail from "@/pages/LeActionDetail";
import LeGoalDetail from "@/pages/LeGoalDetail";
import PublicLeMeeting from "@/pages/PublicLeMeeting";
import LeMinutesPreview from "@/pages/LeMinutesPreview";
import PublicAuditReport from "@/pages/PublicAuditReport";

function Protected({ children, roles, departments, blockProduktion }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Indlæser…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "admin";
  const deps = user.departments || [];
  const prodOnly = !isAdmin && deps.includes("produktion") && !["salg", "indkoeb", "kvalitet"].some((d) => deps.includes(d));
  if (blockProduktion && prodOnly) return <Navigate to="/produktion/svejsning" replace />;
  if (!isAdmin && roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (!isAdmin && departments && !deps.some((d) => departments.includes(d))) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/glemt-adgangskode" element={<ForgotPassword />} />
            <Route path="/aktiver" element={<SetPassword purpose="invite" />} />
            <Route path="/nulstil" element={<SetPassword purpose="reset" />} />
            <Route path="/analyse-preview" element={<AnalyseLayoutPreview />} />
            <Route path="/kunde/:token" element={<PublicProject />} />
            <Route path="/rapport/:token" element={<PublicReport />} />
            <Route path="/kvalitetsrapport-kunde/:token" element={<PublicQualityReport />} />
            <Route path="/projekter-kunde/:token" element={<PublicCustomerProjects />} />
            <Route path="/analyse-deling/:token" element={<PublicAnalysis />} />
            <Route path="/projekter-kunde/:token/:pid" element={<PublicProject />} />
            <Route path="/reklamationer-kunde/:token" element={<PublicCustomerComplaints />} />
            <Route path="/reklamationer-kunde/:token/:rid" element={<PublicQualityReport />} />
            <Route path="/le-referat/:token" element={<PublicLeMeeting />} />
            <Route path="/audit-rapport/:token" element={<PublicAuditReport />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Home />} />
              <Route path="projekter" element={<Protected blockProduktion><Dashboard /></Protected>} />
              <Route path="ressourceplan" element={<Protected blockProduktion><ResourcePlan /></Protected>} />
              <Route path="opret-projekt" element={<Protected roles={["admin", "projektleder"]} departments={["salg"]}><CreateProject /></Protected>} />
              <Route path="projekt/:id" element={<Protected blockProduktion><ProjectDetail /></Protected>} />
              <Route path="admin/skabeloner" element={<Protected roles={["admin"]}><AdminTemplates /></Protected>} />
              <Route path="admin/total-planlaegning" element={<Protected roles={["admin"]}><TotalPlan /></Protected>} />
              <Route path="admin/rapporter" element={<Protected roles={["admin"]}><Reports /></Protected>} />
              <Route path="admin/aktivitet" element={<Protected roles={["admin"]}><AdminActivity /></Protected>} />
              <Route path="admin/dokument-log" element={<Protected roles={["admin"]}><DocumentAccessLog /></Protected>} />
              <Route path="admin/brugere" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
              <Route path="admin/delingslinks" element={<Protected roles={["admin"]}><AdminShareLinks /></Protected>} />
              <Route path="admin/backup" element={<Protected roles={["admin"]}><AdminBackup /></Protected>} />
              <Route path="admin/audit-log" element={<Protected roles={["admin"]}><AdminAuditLog /></Protected>} />
              <Route path="ledelse" element={<Protected roles={["admin"]}><Ledelse /></Protected>} />
              <Route path="ledelse/moede/:id" element={<Protected roles={["admin"]}><LeMeeting /></Protected>} />
              <Route path="ledelse/moede/:id/referat" element={<Protected roles={["admin"]}><LeMinutesPreview /></Protected>} />
              <Route path="handling/:aid" element={<Protected><LeActionDetail /></Protected>} />
              <Route path="maal/:gid" element={<Protected><LeGoalDetail /></Protected>} />
              <Route path="admin/kunder" element={<Protected roles={["admin", "projektleder"]}><AdminCustomers /></Protected>} />
              <Route path="kvalitet" element={<Protected blockProduktion><Cases type="kvalitet" /></Protected>} />
              <Route path="kvalitet/planlaegning" element={<Protected blockProduktion><CasePlan type="kvalitet" /></Protected>} />
              <Route path="kvalitet/opret" element={<Protected blockProduktion><CreateCase type="kvalitet" /></Protected>} />
              <Route path="indkoeb" element={<Protected blockProduktion><Cases type="indkoeb" /></Protected>} />
              <Route path="indkoeb/planlaegning" element={<Protected blockProduktion><CasePlan type="indkoeb" /></Protected>} />
              <Route path="indkoeb/opret" element={<Protected blockProduktion><CreateCase type="indkoeb" /></Protected>} />
              <Route path="indkoeb/leverandoerer" element={<Protected roles={["admin", "projektleder"]}><AdminSuppliers /></Protected>} />
              <Route path="indkoeb/reklamationer" element={<Protected blockProduktion><QualityReports kind="indkob_reklamation" /></Protected>} />
              <Route path="indkoeb/reklamationsanalyse" element={<Protected blockProduktion><ReklamationAnalyse variant="indkob" /></Protected>} />
              <Route path="indkoeb/reklamationsanalyse/analyse/:id" element={<Protected blockProduktion><AnalysisPage /></Protected>} />
              <Route path="kvalitet/reklamationsanalyse/analyse/:id" element={<Protected blockProduktion><AnalysisPage /></Protected>} />
              <Route path="sag/:id" element={<CaseDetail />} />
              <Route path="reklamationer" element={<Protected blockProduktion><QualityReports kind="reklamation" /></Protected>} />
              <Route path="kvalitetsrapporter" element={<Navigate to="/reklamationer" replace />} />
              <Route path="kvalitet/reklamationsanalyse" element={<Protected blockProduktion><ReklamationAnalyse /></Protected>} />
              <Route path="kvalitet/audit" element={<Protected blockProduktion><AuditPlan /></Protected>} />
              <Route path="kvalitet/audit/historik" element={<Protected blockProduktion><AuditHistory /></Protected>} />
              <Route path="kvalitet/audit/fund" element={<Protected blockProduktion><AuditFindings /></Protected>} />
              <Route path="kvalitet/audit/fund/:fid" element={<Protected blockProduktion><FindingDetail /></Protected>} />
              <Route path="kvalitet/audit/:id/udfoer" element={<Protected blockProduktion><AuditExecute /></Protected>} />
              <Route path="kvalitet/audit/:id/rapport" element={<Protected blockProduktion><AuditReport /></Protected>} />
              <Route path="kvalitetsrapport/:id" element={<Protected blockProduktion><QualityReportBuilder /></Protected>} />
              <Route path="dokumenter" element={<Navigate to="/produktion/svejsning" replace />} />
              <Route path="produktion/svejsning" element={<Protected><Documents lockedType="svejsning" /></Protected>} />
              <Route path="produktion/montage" element={<Protected><Documents lockedType="montage" /></Protected>} />
              <Route path="produktion/slibning" element={<Protected><Documents lockedType="slibning" /></Protected>} />
              <Route path="dokument/:id" element={<Protected><DocumentBuilder /></Protected>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
